import React, { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/Brand";
import { checkForUpdateAndReload } from "@/lib/autoUpdate";
import { toast } from "sonner";
import { Info } from "@phosphor-icons/react";
import UpdateBanner from "@/components/UpdateBanner";
import LoginVersionPill from "@/components/LoginVersionPill";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sessionExpired = searchParams.get("session") === "expired";
    const nextPath = searchParams.get("next") || "";
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    if (user) {
        // v1.25.15 — Wipe impersonation on every re-entry to /login, then route
        // the SU to their own Super Dashboard; only honour ?next= if it points
        // inside /admin/super/*. Prevents a tenant-scoped next= URL from
        // dragging the SU back into a company view.
        // v1.29.05 — Skip the wipe on `?session=expired` bounces (the api.js
        // 401 interceptor sends the user here mid-session). Wiping there
        // would drop the SU's tenant context on any transient 401 caused
        // by an aborted-during-reload request. Fresh logins / manual
        // logouts don't carry `?session=expired`, so they still clear it.
        if (!sessionExpired) {
            localStorage.removeItem("micm_impersonate_company");
        }
        if (user.is_super_user) {
            const suNext = nextPath && nextPath.startsWith("/admin/super/") ? nextPath : "/admin/super/dashboard";
            return <Navigate to={suNext} replace />;
        }
        const dest = nextPath && nextPath.startsWith("/") ? nextPath : (["admin", "staff"].includes(user.role) ? "/admin" : "/portal");
        return <Navigate to={dest} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        const raw = identifier.trim();
        if (!raw) {
            setErr("Enter your email or mobile");
            return;
        }
        // Normalize: emails lowercased; mobile stripped to digits (keep last 10)
        const normalized = raw.includes("@") ? raw.toLowerCase() : raw.replace(/\D/g, "").slice(-10);
        if (!raw.includes("@") && normalized.length !== 10) {
            setErr("Mobile must be 10 digits (or use your email)");
            return;
        }
        setBusy(true);
        const res = await login(normalized, password);
        setBusy(false);
        if (res.ok) {
            toast.success("Signed in");
            // v1.25.15 — Clear any prior impersonation on EVERY successful login,
            // regardless of role. Prevents a stale company_id from a previous
            // Super-User session leaking into a plain admin's next login.
            // v1.29.05 — Except when this login is a `?session=expired`
            // bounce — the SU is resuming the same tenant they were in
            // before the reload race; wiping now would defeat the whole
            // point of the v1.28.96 fix. Fresh manual logins never carry
            // the flag so they still hit the clear.
            if (!sessionExpired) {
                localStorage.removeItem("micm_impersonate_company");
            }
            // v1.25.13 — Before we navigate, check the running bundle against
            // /api/version. If the deployed build is newer, hard-reload BEFORE
            // React Router runs any client-side navigation. This is the fix
            // for "SU logs in but sees MD Impex until Ctrl+Shift+R" — the old
            // cached bundle would have applied its old routing rules.
            await checkForUpdateAndReload();
            // v1.25.15 — A Super User is ALWAYS sent to /admin/super/dashboard,
            // except when next= explicitly points inside /admin/super/* (e.g.
            // a session-expiry bounce off /admin/super/companies). We never
            // honour a tenant-scoped next= for a Super User.
            let dest;
            if (res.user.is_super_user) {
                dest = nextPath && nextPath.startsWith("/admin/super/") ? nextPath : "/admin/super/dashboard";
            } else if (nextPath && nextPath.startsWith("/")) {
                dest = nextPath;
            } else {
                dest = ["admin", "staff"].includes(res.user.role) ? "/admin" : "/portal";
            }
            navigate(dest, { replace: true });
        } else {
            setErr(res.error || "Login failed");
            toast.error(res.error || "Login failed");
        }
    };

    return (
        <div className="flex min-h-screen flex-col">
        <UpdateBanner />
        <div className="grid flex-1 grid-cols-1 lg:grid-cols-2">
            {/* Left: form */}
            <div className="flex items-center justify-center bg-white px-6 py-16">
                <div className="w-full max-w-md">
                    <div className="mb-10">
                        <div className="mb-4 flex items-center gap-3">
                            <img
                                src="/logo192.png"
                                alt="MD Impex India"
                                className="h-16 w-16 rounded-md object-cover ring-1 ring-yellow-600/40 shadow-[0_0_18px_rgba(212,165,49,0.35)]"
                            />
                            <Brand size="lg" shimmer sub="Collection Management System" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">
                            Sign in
                        </h1>
                        <p className="mt-3 text-sm text-slate-500">
                            Welcome back. Sign in to continue.
                        </p>
                    </div>

                    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                        {sessionExpired && (
                            <div
                                data-testid="session-expired-banner"
                                className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                            >
                                <Info size={16} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                                <div>
                                    <div className="font-semibold">Your session has expired</div>
                                    <div className="text-xs text-amber-800/85">Please sign in again to continue where you left off.</div>
                                </div>
                            </div>
                        )}
                        <div>
                            <Label htmlFor="identifier" className="text-xs uppercase tracking-[0.2em] text-slate-500">
                                Email or Mobile
                            </Label>
                            <Input
                                id="identifier"
                                data-testid="login-email"
                                type="text"
                                inputMode="email"
                                required
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                autoComplete="username"
                                className="mt-2 h-11"
                                placeholder="you@example.com or 9876543210"
                            />
                            <div className="mt-1 text-[11px] text-slate-500">Use your registered email address or 10-digit mobile number.</div>
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-slate-500">
                                Password
                            </Label>
                            <Input
                                id="password"
                                data-testid="login-password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                className="mt-2 h-11"
                                placeholder="••••••••"
                            />
                        </div>

                        {err && (
                            <div
                                data-testid="login-error"
                                className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                            >
                                {err}
                            </div>
                        )}

                        <Button
                            type="submit"
                            data-testid="login-submit-button"
                            disabled={busy}
                            className="h-11 w-full bg-blue-600 text-white hover:bg-blue-700"
                        >
                            {busy ? "Signing in…" : "Sign in"}
                        </Button>
                    </form>

                    <LoginVersionPill />
                </div>
            </div>

            {/* Right: image */}
            <div
                className="relative hidden lg:block"
                style={{
                    backgroundImage:
                        "url('https://images.unsplash.com/photo-1488972685288-c3fd157d7c7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWwlMjBtaW5pbWFsJTIwYnVpbGRpbmclMjBmYWNhZGV8ZW58MHx8fHwxNzgyMjMyNjQyfDA&ixlib=rb-4.1.0&q=85')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div className="absolute inset-0 bg-slate-900/55" />
                <div className="relative flex h-full flex-col justify-end p-16 text-white">
                    <div className="max-w-md">
                        <div className="mb-3 text-xs uppercase tracking-[0.3em] text-slate-300">Since 2024</div>
                        <h2 className="font-display text-4xl font-bold leading-tight">
                            Collections, ledgers, and payment verification — one professional workspace.
                        </h2>
                        <p className="mt-4 text-sm text-slate-200">
                            Replacing the legacy Excel workflow with a secure, modern platform for parties, transactions and slip verification.
                        </p>
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
}
